
import { AppState } from '@/types';
import { AppAction } from './types';
import { handleLikeActivity, handleCommentActivity, handleUndoLastActivity, handleUndoBulkActivities } from './reducers/activityReducers';
import { handleLogActivity, handleLogEnhancedActivity, handleLogBulkActivity } from './reducers/activityReducers';
import { handleLogin, handleLogout } from './reducers/authReducers';
import { handleUpdateWeeklyGoal, handleResetWeek, handleResetMonth, handleMarkWeeklySummaryShown } from './reducers/weekReducers';
import { handleRedeemPrize } from './reducers/prizeReducers';
import { handleLoadState } from './reducers/stateReducers';
import { handleUpdateUserProfile } from './reducers/userReducers';
import { handleHatchPet, handleFeedPetCollection, handleEvolvePetToMount, handleEquipPet, handleEquipMount, handleUnequipPet, handleUnequipMount, handleAddInventoryItem, handleRemoveInventoryItem, handleInitializePetCollection, handleLoadPetCollection, handleActivityCompletionWithDrops } from './reducers/petCollectionReducers';
import { handleInitializeChallenges, handleUpdateChallengeProgress, handleCompleteChallenge } from './reducers/challengeReducers';
import { habiticaReducer } from './reducers/habiticaReducers';
import { 
  handleCreateSimplePet, 
  handleGivePetAttention, 
  handleEvolveSimplePet, 
  handleUpdateSimplePetPoints,
  handleLinkPetToPledgeSheet,
  handleUpdatePetDailyPerformance,
  handleProcessWeeklyPetSettlement,
  handleCheckPetDailyUpdate,
  handleConsumePetPortion,
  handleCustomizeSimplePet,
  handleFeedSimplePet
} from './reducers/simplePetReducers';

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'UNDO_LAST_ACTIVITY':
      return handleUndoLastActivity(state, action);
    case 'UNDO_BULK_ACTIVITIES':
      return handleUndoBulkActivities(state, action);
      
    case 'LOG_ACTIVITY':
      const newState = handleLogActivity(state, action);
      const stateWithCollection = newState.petCollection ? newState : handleInitializePetCollection(newState);
      const stateWithPetUpdate = stateWithCollection.simplePet 
        ? handleUpdateSimplePetPoints(stateWithCollection, { type: 'UPDATE_SIMPLE_PET_POINTS', payload: { points: action.payload.activityType.points } })
        : stateWithCollection;
      return stateWithPetUpdate;
      
    case 'LOG_BULK_ACTIVITY':
      return handleLogBulkActivity(state, action);
      
    case 'LOG_ENHANCED_ACTIVITY':
      const enhancedState = handleLogEnhancedActivity(state, action);
      const enhancedStateWithCollection = enhancedState.petCollection ? enhancedState : handleInitializePetCollection(enhancedState);
      // Update simple pet with points if it exists
      const enhancedStateWithPetUpdate = enhancedStateWithCollection.simplePet 
        ? handleUpdateSimplePetPoints(enhancedStateWithCollection, { type: 'UPDATE_SIMPLE_PET_POINTS', payload: { points: action.payload.activityType.points } })
        : enhancedStateWithCollection;
      return enhancedStateWithPetUpdate;
      
    case 'LOGIN':
      return handleLogin(state, action);
    case 'LOGOUT':
      return handleLogout();
    case 'UPDATE_WEEKLY_GOAL':
      return handleUpdateWeeklyGoal(state, action);
    case 'UPDATE_USER_PROFILE':
      return handleUpdateUserProfile(state, action);
    case 'RESET_WEEK':
      return handleResetWeek(state);
    case 'REDEEM_PRIZE':
      return handleRedeemPrize(state, action);
    case 'SPEND_ACTIVITY_CREDITS':
      return {
        ...state,
        activityCredits: Math.max(0, state.activityCredits - action.payload.amount)
      };
    case 'LIKE_ACTIVITY':
      return handleLikeActivity(state, action);
    case 'COMMENT_ACTIVITY':
      return handleCommentActivity(state, action);
    case 'LOAD_STATE':
      return handleLoadState(action);

    case 'INITIALIZE_CHALLENGES':
      return handleInitializeChallenges(state);
    case 'UPDATE_CHALLENGE_PROGRESS':
      return handleUpdateChallengeProgress(state, action);
    case 'COMPLETE_CHALLENGE':
      return handleCompleteChallenge(state, action);
    case 'RESET_WEEKLY_STATS':
      return handleResetWeek(state);
    case 'RESET_MONTHLY_STATS':
      return handleResetMonth(state);
    case 'SET_LAST_LOGIN_DATE':
      return { ...state, lastLoginDate: action.payload };
    case 'JOIN_PUBLIC_CHALLENGE':
      return {
        ...state,
        challenges: [...state.challenges, action.payload.challenge],
        activeChallenges: [...state.activeChallenges, action.payload.challenge]
      };
    
    // Habitica pet collection system actions only
    case 'HATCH_PET':
      return handleHatchPet(state, action);
    case 'FEED_PET_COLLECTION':
      return handleFeedPetCollection(state, action);
    case 'EVOLVE_PET_TO_MOUNT':
      return handleEvolvePetToMount(state, action);
    case 'EQUIP_PET':
      return handleEquipPet(state, action);
    case 'EQUIP_MOUNT':
      return handleEquipMount(state, action);
    case 'UNEQUIP_PET':
      return handleUnequipPet(state);
    case 'UNEQUIP_MOUNT':
      return handleUnequipMount(state);
    case 'ADD_INVENTORY_ITEM':
      return handleAddInventoryItem(state, action);
    case 'REMOVE_INVENTORY_ITEM':
      return handleRemoveInventoryItem(state, action);
    case 'INITIALIZE_PET_COLLECTION':
      return handleInitializePetCollection(state);
    case 'LOAD_PET_COLLECTION':
      return handleLoadPetCollection(state, action);
    
    // Simplified pet system actions
    case 'CREATE_SIMPLE_PET':
      return handleCreateSimplePet(state, action);
    case 'GIVE_PET_ATTENTION':
      return handleGivePetAttention(state);
    case 'EVOLVE_SIMPLE_PET':
      return handleEvolveSimplePet(state);
    case 'UPDATE_SIMPLE_PET_POINTS':
      return handleUpdateSimplePetPoints(state, action);
    case 'CONSUME_PET_PORTION':
      return handleConsumePetPortion(state);
    case 'CUSTOMIZE_SIMPLE_PET':
      return handleCustomizeSimplePet(state, action);
    case 'FEED_SIMPLE_PET':
      return handleFeedSimplePet(state, action);
    
    // Pledge-based pet system actions
    case 'LINK_PET_TO_PLEDGE_SHEET':
      return handleLinkPetToPledgeSheet(state, action);
    case 'UPDATE_PET_DAILY_PERFORMANCE':
      return handleUpdatePetDailyPerformance(state, action);
    case 'PROCESS_WEEKLY_PET_SETTLEMENT':
      return handleProcessWeeklyPetSettlement(state, action);
    case 'CHECK_PET_DAILY_UPDATE':
      return handleCheckPetDailyUpdate(state, action);
      
    case 'MARK_WEEKLY_SUMMARY_SHOWN':
      return handleMarkWeeklySummaryShown(state);
    
    // Habitica RPG system actions
    case 'CREATE_HABIT':
    case 'UPDATE_HABIT':
    case 'COMPLETE_HABIT':
    case 'UNCOMPLETE_HABIT':
    case 'DELETE_HABIT':
    case 'CLICK_NEGATIVE_HABIT':
    case 'TAKE_DAMAGE':
    case 'APPLY_DAILY_DAMAGE':
    case 'RESET_DAILIES':
    case 'RESET_DAILY_COUNTERS':
      return habiticaReducer(state, action);
    
    default:
      return state;
  }
}
